import cryptoService from '../services/cryptoService';

/**
 * Parse an encryption key string.
 * Accepts both formats:
 *  - v1: "<64 hex key>iv<32 hex iv>" (legacy AES-CBC keys)
 *  - v2: "<64 hex key>" (AES-GCM keys; the IV lives inside the ciphertext)
 * @param {string} encryptionKey
 * @returns {Object|null} - { key, iv?, version } or null if invalid
 */
export const parseEncryptionKey = (encryptionKey) => {
  if (!encryptionKey) return null;
  const trimmed = encryptionKey.trim();

  const ivIndex = trimmed.indexOf('iv');
  if (ivIndex !== -1) {
    return {
      key: trimmed.substring(0, ivIndex),
      iv: trimmed.substring(ivIndex + 2),
      version: 1,
    };
  }

  if (/^[0-9a-fA-F]{64}$/.test(trimmed)) {
    return { key: trimmed.toLowerCase(), version: 2 };
  }

  return null;
};

/**
 * Whether an encrypted text can only be opened with a passphrase
 * (no key exists anywhere for passphrase-protected documents).
 */
export const isPassphraseProtected = (encryptedText) =>
  cryptoService.detectFormat(encryptedText) === 'v2p';

/**
 * Try to decrypt text with a specific encryption key.
 * @param {string} encryptedText
 * @param {string} encryptionKey - v1 or v2 key string
 * @returns {Promise<Object>} - { success, decryptedText, error, needsPassphrase? }
 */
export const tryDecrypt = async (encryptedText, encryptionKey) => {
  try {
    if (!encryptedText || !encryptionKey) {
      return {
        success: false,
        error: 'Please provide both encrypted content and encryption key'
      };
    }

    const format = cryptoService.detectFormat(encryptedText);

    if (format === 'v2p') {
      return {
        success: false,
        needsPassphrase: true,
        error: 'This document is protected with a passphrase, not a key. Enter the passphrase to decrypt it.'
      };
    }

    const keyParts = parseEncryptionKey(encryptionKey);
    if (!keyParts) {
      return {
        success: false,
        error: 'Invalid encryption key format'
      };
    }

    let decrypted;
    if (format === 'v2') {
      decrypted = await cryptoService.decryptV2({
        encryptedText,
        key: keyParts.key,
      });
    } else {
      // v1 legacy: needs the IV embedded in the key string
      if (!keyParts.iv) {
        return {
          success: false,
          error: 'This document uses the legacy format and needs the full key (with "iv" part)'
        };
      }
      decrypted = cryptoService.decrypt({
        encryptedText,
        key: keyParts.key,
        iv: keyParts.iv
      });
    }

    return {
      success: true,
      decryptedText: decrypted,
      error: null
    };
  } catch (err) {
    return {
      success: false,
      error: 'Failed to decrypt: Invalid encryption key or corrupted data',
      decryptedText: null
    };
  }
};

/**
 * Try to decrypt a passphrase-protected document.
 * @param {string} encryptedText
 * @param {string} passphrase
 * @returns {Promise<Object>} - { success, decryptedText, error }
 */
export const tryDecryptWithPassphrase = async (encryptedText, passphrase) => {
  try {
    if (!encryptedText || !passphrase) {
      return {
        success: false,
        error: 'Please provide both encrypted content and passphrase'
      };
    }
    const decrypted = await cryptoService.decryptWithPassphrase({ encryptedText, passphrase });
    return { success: true, decryptedText: decrypted, error: null };
  } catch (err) {
    return {
      success: false,
      error: 'Failed to decrypt: wrong passphrase or corrupted data',
      decryptedText: null
    };
  }
};

/**
 * Try to decrypt text with multiple keys.
 * @param {string} encryptedText
 * @param {Array} userKeys - Array of key objects from the database/localStorage
 * @returns {Promise<Object>} - { success, decryptedText, keyUsed, error, needsPassphrase? }
 */
export const tryDecryptWithMultipleKeys = async (encryptedText, userKeys) => {
  if (!encryptedText || !userKeys || !userKeys.length) {
    return {
      success: false,
      error: 'No encrypted text or keys provided'
    };
  }

  if (isPassphraseProtected(encryptedText)) {
    return {
      success: false,
      needsPassphrase: true,
      error: 'This document is protected with a passphrase. Enter the passphrase to decrypt it.'
    };
  }

  for (let i = 0; i < userKeys.length; i += 1) {
    const key = userKeys[i];
    try {
      const result = await tryDecrypt(encryptedText, key.encryption_key);
      if (result.success) {
        return {
          ...result,
          keyUsed: key.encryption_key
        };
      }
    } catch (err) {
      continue;
    }
  }

  return {
    success: false,
    error: 'Could not decrypt with any of your saved keys. Try to upload or scan the key.',
    decryptedText: null,
    keyUsed: null
  };
};
